import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CustomSyncService } from '../../core/services/custom-sync.service';
import { LoggerService } from '../../core/services/logger.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardActionsComponent } from '../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { EnvPrefixPipe } from '../../shared/pipes/env-prefix.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Generic landing page shown once a user is authenticated and has an
 * organization. This baseline has no entity-specific action buttons —
 * a concrete app built on this platform should add its own here (see how
 * this component worked pre-refactor for the pattern: an entity count
 * query gates the onboarding redirect/link, and the card actions link out
 * to the app's key entity list pages).
 */
@Component({
  selector: 'app-home',
  imports: [
    HasPermissionDirective,
    RouterLink,
    TranslatePipe,
    EnvPrefixPipe,
    CardComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ButtonComponent,
  ],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit {
  private router = inject(Router);
  private userStorage = inject(UserStorageService);
  private syncService = inject(CustomSyncService);
  private logger = inject(LoggerService).getLogger('home');

  showOnboardingLink = signal(false);

  async ngOnInit() {
    // CRITICAL: Wait for sync service to be ready before querying database
    const syncConnected = await this.syncService.connected();
    if (!syncConnected) {
      this.logger.warn('Sync not connected - cannot load dashboard data');
      return;
    }

    const onboardingDismissed =
      this.userStorage.getTypedItem<boolean>('onboarding_dismissed') ??
      this.userStorage.getGlobalTypedItem<boolean>('onboarding_dismissed') ??
      false;

    // Redirect to onboarding if the user hasn't dismissed it yet
    if (!onboardingDismissed) {
      this.router.navigate(['/onboarding']);
      return;
    }

    this.showOnboardingLink.set(false);
  }
}
